import { ft } from '../core/units.js';
import { definition } from './mass.js';

/**
 * Snapping what you carry to the plan: the nearest spot on the event's diagram,
 * within reach, that wants what is in your hands.
 *
 * One button (C, or RT on a pad) instead of lining a round up on a mark by
 * eye. It only finds the spot; setting it goes through `setup.place`, the same
 * call the action key's "set" makes, so a snap can never put anything anywhere
 * the key could not. What it saves is the shuffling, not the walk: the reach
 * is a little over one grid square, so you still carry every round across the
 * room yourself.
 *
 * "Correct" is the diagram's: the BEO's `layout` block of the grid, the same
 * cells `roomDiagram.js` draws gold.
 *
 *   a round       an empty cell of the block
 *   a linen       a set round in the block with nothing on it
 *   a chair       a set round in the block short of its chairs
 *
 * Nearest first; a cell the block does not want is never offered, and neither
 * is the dance floor. Pure: reads the setup, the hands and the spec.
 */

/** How far a snap reaches: a grid square (twelve feet) and a little over. */
export const SNAP_REACH = ft(14);

/** The cells the plan wants, as anchor keys, or null for "any cell" when there is no plan. */
export function plannedKeys(spec) {
  const layout = spec?.layout;
  if (!layout) return null;
  const keys = new Set();
  for (let c = layout.col; c < layout.col + layout.cols; c += 1) {
    for (let r = layout.row; r < layout.row + layout.rows; r += 1) keys.add(`${c},${r}`);
  }
  return keys;
}

/**
 * The spot a snap would use, or null with a reason.
 *
 * Back comes `{ anchor, role, distance }` to set, or `{ anchor: null, why }`
 * when there is nothing in reach that wants it.
 */
export function snapTarget({ setup, carry, position, spec, reach = SNAP_REACH }) {
  const item = carry?.kind ? definition(carry.kind) : null;
  const rule = item?.place;
  if (!rule || !carry.count) return { anchor: null, why: null };
  const wanted = plannedKeys(spec);
  const chairs = spec?.chairsPerTable ?? rule.max ?? Infinity;

  let best = null;
  for (const anchor of setup.anchors) {
    if (anchor.keepClear) continue;
    if (wanted && !wanted.has(anchor.key)) continue;
    const table = setup.tableAt(anchor.key);
    if (rule.onto === 'anchor') {
      if (table) continue;
    } else if (!table) {
      continue;
    } else if (rule.role === 'cover') {
      if (table.cover) continue;
    } else if (table.seats >= Math.min(chairs, rule.max ?? Infinity)) {
      continue;
    }
    const distance = Math.hypot(position.x - anchor.x, position.z - anchor.z);
    if (distance > reach) continue;
    if (!best || distance < best.distance) best = { anchor, role: rule.role, distance };
  }
  if (best) return best;
  const what = (item.plural ?? `${item.name}s`).toLowerCase();
  return { anchor: null, why: `No spot on the plan wants ${what} within reach.` };
}
